import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Upload, Link as LinkIcon, FileType2, Loader2, ScanLine, Trash2, History, GitCompare } from "lucide-react";
import VersionsModal from "./VersionsModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { openDrivePicker } from "@/lib/googlePicker";

const SEV = {
  low:      "text-emerald-700 bg-emerald-50",
  medium:   "text-amber-700 bg-amber-50",
  high:     "text-orange-700 bg-orange-50",
  critical: "text-rose-700 bg-rose-50",
};

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [versionsModalDoc, setVersionsModalDoc] = useState(null);
  const [groupScores, setGroupScores] = useState({});  // {group_id: [{version_number, score, level}]}
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [docsRes, scoresRes] = await Promise.allSettled([
        api.get("/documents"),
        api.get("/documents/version-scores"),
      ]);
      if (docsRes.status === "fulfilled") setDocs(docsRes.value.data);
      else throw docsRes.reason;
      if (scoresRes.status === "fulfilled") setGroupScores(scoresRes.value.data?.groups || {});
    } catch (err) {
      toast.error("Couldn't load documents", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Surface the result of the Google Drive OAuth round-trip. The callback
  // endpoint redirects here with one of these query flags; we toast + clean
  // them out of the URL so a refresh doesn't re-trigger.
  useEffect(() => {
    if (searchParams.get("google_connected") === "1") {
      toast.success("Google Drive connected", {
        description: "Click \"Import from Google Drive\" to pick files.",
      });
      searchParams.delete("google_connected");
      setSearchParams(searchParams, { replace: true });
    }
    const err = searchParams.get("google_error");
    if (err) {
      toast.error("Couldn't connect Google Drive", { description: err });
      searchParams.delete("google_error");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runScan = async (doc) => {
    try {
      const { data } = await api.post("/scans/run", { document_id: doc.id });
      toast.info("Scan queued", { description: "Analyzing — this usually takes 10–40 seconds." });
      navigate(`/scans/${data.id}`, { state: { initial: data } });
    } catch (err) {
      if (err?.response?.status === 402) {
        const detail = err.response.data?.detail || {};
        toast.error(detail.message || "Upgrade required", {
          description: "Click here to view plans →",
          action: { label: "Upgrade", onClick: () => navigate("/billing") },
          duration: 8000,
        });
      } else {
        toast.error("Couldn't start scan", { description: formatApiError(err) });
      }
    }
  };

  const remove = async (doc) => {
    try {
      await api.delete(`/documents/${doc.id}`);
      setDocs((ds) => ds.filter((d) => d.id !== doc.id));
      toast.success("Document deleted");
    } catch (err) {
      toast.error("Delete failed", { description: formatApiError(err) });
    }
  };

  return (
    <div className="space-y-8" data-testid="documents-page">
      <div>
        <p className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600">Documents</p>
        <h1 className="mt-1.5 font-display text-3xl font-semibold tracking-tight text-slate-900">
          Add documents to scan
        </h1>
        <p className="mt-1.5 text-sm text-slate-600">
          Upload PDFs and Word docs, import a public URL, or paste text directly. Max 5 MB per file.
        </p>
      </div>

      <GoogleDriveImport onImported={load} />

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <Tabs defaultValue="file">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="file" data-testid="doc-tab-file">File</TabsTrigger>
            <TabsTrigger value="url" data-testid="doc-tab-url">URL</TabsTrigger>
            <TabsTrigger value="text" data-testid="doc-tab-text">Paste text</TabsTrigger>
          </TabsList>
          <TabsContent value="file"><UploadFile onAdded={load} /></TabsContent>
          <TabsContent value="url"><AddUrl onAdded={load} /></TabsContent>
          <TabsContent value="text"><AddText onAdded={load} /></TabsContent>
        </Tabs>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="documents-list">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.12em] text-slate-500 font-semibold">
            <tr>
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Source</th>
              <th className="px-5 py-3">Size</th>
              <th className="px-5 py-3">Last scan</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" /> Loading…
              </td></tr>
            )}
            {!loading && docs.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">
                No documents yet. Upload your first one above.
              </td></tr>
            )}
            {docs.map((d) => (
              <tr key={d.id} className="hover:bg-slate-50" data-testid={`doc-row-${d.id}`}>
                <td className="px-5 py-3">
                  <div className="font-medium text-slate-900 flex items-center gap-2">
                    {d.name}
                    {d.version_number && d.version_number > 0 && (
                      <span
                        className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 px-2 py-0.5 text-[10px] font-semibold"
                        data-testid={`doc-version-badge-${d.id}`}
                      >
                        v{d.version_number}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-5 py-3 text-slate-600 capitalize">{d.source}</td>
                <td className="px-5 py-3 text-slate-600">{(d.char_count || 0).toLocaleString()} chars</td>
                <td className="px-5 py-3">
                  <div className="inline-flex items-center gap-2">
                    {d.last_scan_id ? (
                      <span className={"rounded-full px-2 py-0.5 text-[11px] font-semibold " + (SEV[d.last_scan_level] || SEV.medium)}>
                        {d.last_scan_level} · {d.last_scan_score}
                      </span>
                    ) : <span className="text-slate-400">Never</span>}
                    {d.version_group_id && (groupScores[d.version_group_id]?.filter((p) => p.score != null).length >= 2) && (
                      <ScoreSparkline
                        points={groupScores[d.version_group_id]}
                        currentDocId={d.id}
                        onClick={() => setVersionsModalDoc(d)}
                        testId={`doc-sparkline-${d.id}`}
                      />
                    )}
                  </div>
                </td>
                <td className="px-5 py-3 text-right space-x-2">
                  <Button
                    size="sm" onClick={() => setVersionsModalDoc(d)}
                    variant="outline"
                    className="rounded-md border-slate-300 text-slate-700"
                    data-testid={`doc-versions-${d.id}`}
                  >
                    <History className="h-3.5 w-3.5 mr-1.5" /> Versions
                  </Button>
                  <Button
                    size="sm" onClick={() => runScan(d)}
                    className="rounded-md bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid={`doc-scan-${d.id}`}
                  >
                    <ScanLine className="h-3.5 w-3.5 mr-1.5" /> Scan
                  </Button>
                  {d.last_scan_id && (
                    <Button size="sm" variant="outline" onClick={() => navigate(`/scans/${d.last_scan_id}`)} className="rounded-md">
                      View
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => remove(d)} className="rounded-md text-slate-500 hover:text-rose-600" data-testid={`doc-delete-${d.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {versionsModalDoc && (
        <VersionsModal
          doc={versionsModalDoc}
          onClose={() => setVersionsModalDoc(null)}
          onReload={() => { load(); setVersionsModalDoc(null); }}
        />
      )}
    </div>
  );
}

function UploadFile({ onAdded }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [versionPrompt, setVersionPrompt] = useState(null);   // {child, match}
  const upload = async () => {
    const file = ref.current?.files?.[0];
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      // Explicit multipart override needed because the shared axios instance
      // defaults to Content-Type: application/json. Browser/axios will add
      // the boundary= parameter automatically when given a FormData body.
      const { data } = await api.post("/documents/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      ref.current.value = "";
      // Auto-detect: if the upload response carries a version_match the
      // server thinks this file is a new version of an existing doc.
      // Defer the success toast and surface a prompt instead — the user
      // can accept (link as a version) or dismiss (keep standalone).
      if (data?.version_match) {
        setVersionPrompt({ child: data, match: data.version_match });
      } else {
        toast.success("Document added");
      }
      onAdded();
    } catch (err) {
      toast.error("Upload failed", { description: formatApiError(err) });
    } finally { setBusy(false); }
  };
  const linkAsVersion = async () => {
    if (!versionPrompt) return;
    try {
      await api.post(`/documents/${versionPrompt.child.id}/link-version`, {
        parent_id: versionPrompt.match.parent_id,
      });
      toast.success("Linked as new version", {
        description: `Saved as v${versionPrompt.match.parent_version_number + 1}`,
      });
      onAdded();
    } catch (err) {
      toast.error("Couldn't link version", { description: formatApiError(err) });
    } finally {
      setVersionPrompt(null);
    }
  };
  return (
    <>
      <div className="pt-6 flex flex-col sm:flex-row items-stretch gap-3">
        <Input ref={ref} type="file" accept=".pdf,.docx,.txt,.md" className="h-11" data-testid="doc-file-input" />
        <Button onClick={upload} disabled={busy} className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white sm:px-6 h-11" data-testid="doc-file-upload">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-2" />Upload</>}
        </Button>
      </div>
      {versionPrompt && (
        <div
          className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4"
          data-testid="version-prompt"
        >
          <div className="flex items-start gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-white shrink-0">
              <History className="h-4 w-4" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900">
                Looks like a new version
              </div>
              <p className="text-sm text-slate-700 mt-1">
                We found an earlier document called{" "}
                <span className="font-medium text-slate-900">"{versionPrompt.match.parent_name}"</span>{" "}
                with the same filename. Save this upload as{" "}
                <span className="font-semibold">v{versionPrompt.match.parent_version_number + 1}</span>{" "}
                of that document?
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={linkAsVersion}
                  data-testid="version-prompt-accept"
                >
                  Link as v{versionPrompt.match.parent_version_number + 1}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-slate-600"
                  onClick={() => setVersionPrompt(null)}
                  data-testid="version-prompt-dismiss"
                >
                  Keep as a separate document
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function AddUrl({ onAdded }) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    setBusy(true);
    try {
      await api.post("/documents/url", { name: name || url, url });
      toast.success("URL imported");
      setName(""); setUrl("");
      onAdded();
    } catch (err) {
      toast.error("Import failed", { description: formatApiError(err) });
    } finally { setBusy(false); }
  };
  return (
    <div className="pt-6 grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-3">
      <Input placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} className="h-11" data-testid="doc-url-name" />
      <Input placeholder="https://example.com/privacy" value={url} onChange={(e) => setUrl(e.target.value)} className="h-11" data-testid="doc-url-input" />
      <Button onClick={add} disabled={busy || !url} className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white h-11 px-6" data-testid="doc-url-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LinkIcon className="h-4 w-4 mr-2" />Import</>}
      </Button>
    </div>
  );
}

function AddText({ onAdded }) {
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    setBusy(true);
    try {
      await api.post("/documents/text", { name: name || "Pasted text", text });
      toast.success("Text added");
      setName(""); setText("");
      onAdded();
    } catch (err) {
      toast.error("Add failed", { description: formatApiError(err) });
    } finally { setBusy(false); }
  };
  return (
    <div className="pt-6 space-y-3">
      <Input placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} className="h-11" data-testid="doc-text-name" />
      <Textarea placeholder="Paste your policy, contract or terms here…" value={text} onChange={(e) => setText(e.target.value)} rows={6} data-testid="doc-text-input" />
      <Button onClick={add} disabled={busy || text.trim().length < 20} className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white h-11 px-6" data-testid="doc-text-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileType2 className="h-4 w-4 mr-2" />Add text</>}
      </Button>
    </div>
  );
}


/**
 * "Import from Google Drive" card — sits above the upload tabs as the
 * primary CTA. Handles three states transparently:
 *  - First load: checks /status to know whether the user is connected.
 *  - Not connected: button label "Connect Google Drive" → kicks off OAuth.
 *  - Connected: button "Import from Google Drive" → opens the Google Picker
 *    → POSTs the selected file IDs to /import → refreshes the document list.
 *
 * Errors during OAuth come back as ?google_error= query params (handled by
 * the parent page). Errors during import are surfaced inline.
 */
function GoogleDriveImport({ onImported }) {
  const [status, setStatus] = React.useState(null); // {connected, email}
  const [statusLoading, setStatusLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);

  const refreshStatus = async () => {
    try {
      const { data } = await api.get("/integrations/google/status");
      setStatus(data);
    } catch {
      setStatus({ connected: false });
    } finally { setStatusLoading(false); }
  };
  React.useEffect(() => { refreshStatus(); }, []);

  const startOauth = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/integrations/google/connect");
      window.location.assign(data.authorization_url);
    } catch (err) {
      toast.error("Couldn't start Google sign-in", { description: formatApiError(err) });
      setBusy(false);
    }
  };

  const importFromDrive = async () => {
    setBusy(true);
    try {
      const { data: pt } = await api.get("/integrations/google/picker-token");
      const files = await openDrivePicker({
        accessToken: pt.access_token,
        apiKey: pt.api_key,
      });
      if (!files || files.length === 0) { setBusy(false); return; }
      const { data } = await api.post("/integrations/google/import", {
        file_ids: files.map((f) => f.id),
      });
      const imported = data.imported || [];
      const errors = data.errors || [];
      if (imported.length) {
        toast.success(
          `Imported ${imported.length} file${imported.length === 1 ? "" : "s"} from Google Drive`,
          errors.length
            ? { description: `${errors.length} file${errors.length === 1 ? "" : "s"} skipped` }
            : undefined,
        );
        onImported();
      }
      if (errors.length) {
        errors.slice(0, 2).forEach((e) =>
          toast.error("Couldn't import a file", { description: e.error }),
        );
      }
    } catch (err) {
      const msg = formatApiError(err);
      if (err?.response?.status === 400 && msg.includes("not connected")) {
        // Stale credentials — re-prompt.
        toast.error("Google Drive disconnected", { description: "Please reconnect to continue." });
        await refreshStatus();
      } else {
        toast.error("Google Drive import failed", { description: msg });
      }
    } finally { setBusy(false); }
  };

  const connected = !!status?.connected;
  return (
    <div
      className="rounded-2xl border border-slate-200 bg-gradient-to-br from-blue-50/40 via-white to-white p-5 sm:p-6 shadow-sm"
      data-testid="google-drive-card"
    >
      <div className="flex items-center gap-4 flex-wrap">
        <div className="shrink-0 h-11 w-11 rounded-xl bg-white ring-1 ring-slate-200 flex items-center justify-center">
          <GoogleDriveIcon className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-base sm:text-lg font-semibold text-slate-900">
            Import from Google Drive
          </h2>
          <p className="mt-0.5 text-xs sm:text-sm text-slate-600">
            {connected ? (
              <>
                Connected as{" "}
                <span className="font-medium text-slate-900" data-testid="drive-account-email">
                  {status.email || "your Google account"}
                </span>{" "}
                · Browse files and pick PDFs, Word docs, or Google Docs to scan.
              </>
            ) : (
              "Connect your Google Drive once, then pick contracts to scan in seconds — no manual download required."
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {statusLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          ) : connected ? (
            <Button
              onClick={importFromDrive}
              disabled={busy}
              className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white h-10 px-4 sm:px-5"
              data-testid="drive-import-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>
                <GoogleDriveIcon className="h-4 w-4 mr-2" />
                Import from Google Drive
              </>}
            </Button>
          ) : (
            <Button
              onClick={startOauth}
              disabled={busy}
              className="rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-slate-900 h-10 px-4 sm:px-5"
              data-testid="drive-connect-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>
                <GoogleDriveIcon className="h-4 w-4 mr-2" />
                Connect Google Drive
              </>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Compact, on-brand Google Drive triangle mark (avoids pulling in a logo asset).
function GoogleDriveIcon({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 87.3 78" className={className} aria-hidden="true">
      <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
      <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3L1.2 47.05a9.06 9.06 0 0 0-1.2 4.5h27.5z" fill="#00ac47" />
      <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502L65.5 76.8z" fill="#ea4335" />
      <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
      <path d="m59.8 51.55h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
      <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00" />
    </svg>
  );
}


/**
 * Tiny inline sparkline showing the score trend across versions of a contract.
 * Plots score (0–100, higher = safer) over version_number on a 56×18px SVG.
 * Last point gets a filled dot; the current row's version is highlighted in blue.
 * Click bubbles up to open the VersionsModal for full diff.
 */
function ScoreSparkline({ points, currentDocId, onClick, testId }) {
  const scored = (points || []).filter((p) => p.score != null);
  if (scored.length < 2) return null;
  const W = 56, H = 18, PAD = 2;
  const minV = 0, maxV = 100;
  const xs = (i) => PAD + (i * (W - PAD * 2)) / Math.max(1, scored.length - 1);
  const ys = (v) => H - PAD - ((v - minV) * (H - PAD * 2)) / (maxV - minV);
  const path = scored.map((p, i) => `${i === 0 ? "M" : "L"}${xs(i).toFixed(1)} ${ys(p.score).toFixed(1)}`).join(" ");
  // Trend tone: compare first vs last scored point. Higher score = safer.
  const delta = scored[scored.length - 1].score - scored[0].score;
  const tone = delta >= 5 ? "stroke-emerald-500" : delta <= -5 ? "stroke-rose-500" : "stroke-slate-400";
  const title = scored
    .map((p) => `v${p.version_number}: ${p.score}`)
    .join("  →  ") + (delta === 0 ? "" : `  (${delta > 0 ? "+" : ""}${delta})`);
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Score evolution: ${title}`}
      className="inline-flex items-center align-middle rounded hover:bg-slate-100 transition px-1 py-0.5"
      data-testid={testId}
    >
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
        <path d={path} fill="none" className={tone} strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round" />
        {scored.map((p, i) => {
          const isCurrent = p.doc_id === currentDocId;
          const isLast = i === scored.length - 1;
          if (!isCurrent && !isLast) return null;
          return (
            <circle
              key={p.doc_id}
              cx={xs(i)} cy={ys(p.score)} r={isCurrent ? 2.2 : 1.8}
              className={isCurrent ? "fill-blue-600" : "fill-slate-700"}
            />
          );
        })}
      </svg>
    </button>
  );
}
